import type { Currency, FamilyRole, LedgerEntry } from "../api/types"

const compactNumber = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 })
const dateTime = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit"
})
const dateOnly = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" })

export const ROLE_LABELS: Record<FamilyRole, string> = {
  OWNER: "Владелец",
  ADMIN: "Администратор",
  MEMBER: "Участник"
}

export const SCOPE_LABELS: Record<Currency["scope"], string> = {
  FAMILY_POOL: "Общая копилка",
  PAIR: "Личная договорённость",
  PORTABLE: "Можно передавать"
}

export const OPERATION_LABELS: Record<string, string> = {
  ISSUE: "Начисление",
  ISSUED: "Начисление",
  TRANSFER: "Перевод",
  TRANSFERRED: "Перевод",
  SPEND: "Покупка",
  SPENT: "Покупка",
  PENALTY: "Штраф",
  PENALTY_APPLIED: "Штраф",
  EXPIRY: "Сгорание",
  DECAY: "Уменьшение",
  ADJUSTMENT: "Корректировка",
  REVERSAL: "Возврат"
}

export function getCurrencyBalance(currency: Currency, memberId?: string): string {
  if (typeof currency.balance === "string") return currency.balance
  if (currency.balance?.amount) return currency.balance.amount
  if (currency.totalBalance !== undefined) return currency.totalBalance
  if (currency.balances?.length) {
    const own = memberId ? currency.balances.find((balance) => balance.memberId === memberId) : undefined
    if (own) return own.available ?? own.amount
    return currency.balances.reduce((sum, balance) => sum + BigInt(balance.available ?? balance.amount), 0n).toString()
  }
  if (currency.accounts?.length) {
    const own = memberId ? currency.accounts.find((account) => account.memberId === memberId) : undefined
    if (own) return own.available ?? own.balance ?? own.amount ?? "0"
    const pool = currency.accounts.find((account) => account.kind === "FAMILY_POOL")
    if (pool) return pool.available ?? pool.balance ?? pool.amount ?? "0"
    return currency.accounts
      .filter((account) => !["MINT", "BURN"].includes(account.kind ?? ""))
      .reduce((sum, account) => sum + BigInt(account.available ?? account.balance ?? account.amount ?? "0"), 0n)
      .toString()
  }
  return "0"
}

export function formatAmount(minor: string | number | bigint | undefined, scale = 0): string {
  let value: bigint
  try {
    value = BigInt(minor ?? 0)
  } catch {
    return "0"
  }
  const negative = value < 0n
  const absolute = negative ? -value : value
  if (scale === 0) return `${negative ? "−" : ""}${absolute.toLocaleString("ru-RU")}`
  const base = 10n ** BigInt(scale)
  const whole = absolute / base
  const fraction = (absolute % base).toString().padStart(scale, "0").replace(/0+$/, "")
  return `${negative ? "−" : ""}${whole.toLocaleString("ru-RU")}${fraction ? `,${fraction}` : ""}`
}

export function toMinorUnits(input: string, scale: number): string | null {
  const normalized = input.trim().replace(",", ".").replace(/\s/g, "")
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null
  const [whole = "0", decimal = ""] = normalized.split(".")
  if (decimal.length > scale) return null
  return `${whole}${decimal.padEnd(scale, "0")}`.replace(/^0+(?=\d)/, "")
}

export function formatCurrencyAmount(currency: Currency, amount?: string) {
  return `${formatAmount(amount ?? getCurrencyBalance(currency), currency.scale)} ${currency.symbol}`.trim()
}

export function formatCompact(amount: string, scale = 0): string {
  const value = Number(amount) / 10 ** scale
  if (!Number.isFinite(value)) return formatAmount(amount, scale)
  const absolute = Math.abs(value)
  if (absolute >= 1_000_000_000) return `${compactNumber.format(value / 1_000_000_000)} млрд`
  if (absolute >= 1_000_000) return `${compactNumber.format(value / 1_000_000)} млн`
  if (absolute >= 1_000) return `${compactNumber.format(value / 1_000)}к`
  return compactNumber.format(value)
}

export function formatDateTime(value?: string) {
  if (!value) return "только что"
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "—" : dateTime.format(date)
}

export function formatDate(value?: string) {
  if (!value) return "—"
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "—" : dateOnly.format(date)
}

export function formatRelative(value?: string) {
  if (!value) return "только что"
  const delta = Date.now() - new Date(value).getTime()
  if (!Number.isFinite(delta) || delta < 0) return formatDateTime(value)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  if (delta < minute) return "только что"
  if (delta < hour) return `${Math.floor(delta / minute)} мин назад`
  if (delta < day) return `${Math.floor(delta / hour)} ч назад`
  if (delta < day * 7) return `${Math.floor(delta / day)} дн назад`
  return formatDateTime(value)
}

export function signedEntryAmount(entry: LedgerEntry): string {
  if (entry.signedAmount !== undefined) return entry.signedAmount
  if (entry.amount !== undefined) return entry.amount
  const relevant = entry.entries?.filter((posting) => !["MINT", "BURN"].includes(posting.account?.kind ?? "")) ?? []
  if (!relevant.length) return "0"
  return relevant
    .reduce((maximum, posting) => {
      const value = BigInt(posting.amount)
      const absolute = value < 0n ? -value : value
      return absolute > maximum ? absolute : maximum
    }, 0n)
    .toString()
}

export function initials(name?: string) {
  if (!name) return "СК"
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}

export function humanError(error: unknown) {
  return error instanceof Error ? error.message : "Не удалось выполнить действие"
}
